import { Dispatch, SetStateAction } from 'react';
import { FileViewer } from 'src/@carketa/file-viewer/FileViewer';
import GoogleMapComponent from 'src/@carketa/sales-packet/components/GoogleMapWithMarker';
import { CarketaDisclosure } from 'src/@carketa/sales-packet/components/CarketaDisclosure';
import { Button } from 'src/ui/button';
import { Typography } from 'src/ui/typography';
import type { OrganizationSettings } from 'src/types/organizations/organizations';
import { normalizeExternalUrl } from 'src/utils/externalWebsiteUrl';
import { cn } from 'src/utils/@/lib/utils';
import { Mail, Phone, Pin } from 'lucide-react';

type VehicleDetailsSectionProps = {
  vehicle: any;
  pdfMode?: boolean;
  setSalesLeadOpen?: Dispatch<SetStateAction<boolean>> | undefined;
};

const VehicleDetailsSection = ({ vehicle, pdfMode, setSalesLeadOpen }: VehicleDetailsSectionProps) => {
  const org_settings = vehicle.organization.settings as OrganizationSettings;
  const locationWebsiteUrl = normalizeExternalUrl(vehicle.location?.website);
  const vehicleDetailsWidget = (icon: any, title: string, desc: string) => {
    return (
      <div className="flex w-1/4 items-start">
        <div>
          <Typography className="text-black">{title}</Typography>
          <Typography variant="caption" className="break-words text-muted-foreground">
            {desc}
          </Typography>
        </div>
      </div>
    );
  };

  return (
    <div id="vehicle-details" className="flex flex-col items-center justify-center gap-6">
      <div className="w-full p-2">
        <div className="xs:flex-col flex w-full max-w-[1000px] items-center justify-between sm:flex-row">
          <div className="flex">
            <div>
              <h1 className="text-center text-lg">
                {vehicle.year} {vehicle.make} {vehicle.model} {vehicle.trim}
              </h1>
              <p className="gray-500 text-center text-sm">{vehicle.vin}</p>
            </div>
          </div>

          <div className="xs:mt-[10px] xs:flex-col flex justify-center sm:mt-[0px] sm:flex-row">
            {!org_settings.remove_price_on_sales_packet && (
              <div className="mr-4">
                <h1 className="text-center text-xl">${vehicle.listed_price}</h1>
                <p className="gray-500 text-center text-sm">
                  {vehicle.mileage} {org_settings?.use_kilometers ? 'Kilometers' : 'Miles'}
                </p>
              </div>
            )}
            {!pdfMode && setSalesLeadOpen && (
              <Button
                variant="contained"
                color="success"
                className={cn(!vehicle.location.lead_sales_email && 'hidden', 'mt-2.5 sm:mt-0')}
                onClick={() => setSalesLeadOpen(true)}
              >
                CHECK AVAILABILITY
              </Button>
            )}
          </div>
        </div>
      </div>
      <div className="w-full p-2">
        <div className="mb-12">
          <FileViewer
            id={vehicle?.id ?? ''}
            showPlaceholder={false}
            showThumbnails={false}
            allowUpload={false}
            showMenu={false}
            pdfMode={pdfMode}
          />
        </div>
      </div>
      <div className="w-full p-2">
        <div className="mb-8 w-full">
          <Typography variant="h6" className="mb-10 text-black">
            Vehicle Details
          </Typography>
          <div className="mb-6 flex justify-between">
            {vehicleDetailsWidget('engineIcon', 'Engine', vehicle.engine ?? 'N/A')}
            {vehicleDetailsWidget('bodyStyleIcon', 'Body Style', vehicle.body_style ?? 'N/A')}
            {vehicleDetailsWidget('exteriorIcon', 'Exterior Color', vehicle.exterior_color ?? 'N/A')}
          </div>
          <div className="mb-6 flex justify-between">
            {vehicleDetailsWidget('interiorIcon', 'Interior', vehicle.interior_color ?? 'N/A')}
            {vehicleDetailsWidget('transmissionIcon', 'Transmission', vehicle.transmission ?? 'N/A')}
            {vehicleDetailsWidget('vinIcon', 'VIN', vehicle.vin ?? 'N/A')}
          </div>
          <div className="flex justify-between">
            {vehicleDetailsWidget('stockNumberIcon', 'Stock Number', vehicle.stocknumber ?? 'N/A')}
            {vehicleDetailsWidget('mileageIcon', 'Mileage', vehicle.mileage ?? 'N/A')}
            {vehicleDetailsWidget('trimIcon', 'Trim', vehicle.trim ?? 'N/A')}
          </div>
        </div>
        <div className="mb-8 w-full pl-2">
          <Typography className="mb-4 text-md text-black">Description</Typography>
          <Typography variant="caption" className="text-muted-foreground">
            {vehicle.description ?? 'N/A'}
          </Typography>
        </div>
      </div>
      <div className="flex w-full flex-col p-2">
        <div className="align-middle flex flex-wrap gap-5 lg:flex-nowrap">
          {pdfMode && (
            <div className="flex w-full flex-col p-3 lg:w-1/3">
              <Typography variant="h6" className="mb-10 text-black">
                Dealership Location
              </Typography>
              <p className="gray-500 mb-2 text-sm">{vehicle.location.name} </p>
              <div className="flex items-center">
                <Pin className="mr-2 h-5 w-5" />
                <p className="gray-400 mb-1 text-sm">
                  {vehicle.location.stree1} {vehicle.location.city}, {vehicle.location.state} {vehicle.location.zip}
                </p>
              </div>
              <div className="flex items-center">
                <Mail className={cn('mr-2 h-5 w-5', !vehicle.location.contact_email && 'hidden')} />
                <p className="gray-400 mb-1 text-sm">{vehicle.location.contact_email}</p>
              </div>
              <div className="m-1 flex items-center">
                <Phone className={cn('mr-2 h-5 w-5', !vehicle.location.contact_phone && 'hidden')} />
                <p className="gray-400 mb-1 text-sm">{vehicle.location.contact_phone}</p>
              </div>
              {locationWebsiteUrl && (
                <div className="m-1 flex items-center">
                  <a href={locationWebsiteUrl}>
                    <p className="gray-400 mb-1 text-sm">{locationWebsiteUrl}</p>
                  </a>
                </div>
              )}
            </div>
          )}
          <div className={`w-full ${pdfMode ? 'lg:w-2/3' : 'lg:w-full'}`}>
            <GoogleMapComponent lat={vehicle.location.latitude} lng={vehicle.location.longitude} />
          </div>
        </div>
      </div>
      <div className={cn(!pdfMode && 'hidden')}>
        <CarketaDisclosure />
      </div>
    </div>
  );
};

export default VehicleDetailsSection;
